// Lista de presupuestos, del más nuevo al más viejo.

import { api } from "../api";
import { esc } from "../html";
import { iconoMas } from "../iconos";
import { conmutadorPrecios } from "./contratos";
import { ETIQUETA_ESTADO_PRESUPUESTO, claseEstadoPresupuesto } from "../etiquetas";
import { MONEDAS } from "../../lib/tipos";
import type { Cliente, Presupuesto, ProyectoListado } from "../../lib/tipos";
import { formatearMonto } from "../../lib/calculo";
import { formatoLocal } from "../../lib/fecha";

function fila(p: Presupuesto): string {
  const apagado = p.estado === "ANULADO" || p.estado === "RECHAZADO" ? "opacity-60" : "";
  return `
    <a href="#/presupuesto/${p.id}" class="fila block p-4 ${apagado}">
      <div class="flex items-start gap-2">
        <div class="min-w-0 flex-1">
          <div class="flex items-baseline gap-2">
            <span class="font-mono text-sm text-apagado">Nº ${p.numero}</span>
            <span class="truncate font-medium">${esc(p.cliente_nombre)}</span>
          </div>
          <div class="mt-0.5 truncate text-xs text-apagado">
            ${formatoLocal(p.fecha)}${p.proyecto_nombre ? " · " + esc(p.proyecto_nombre) : ""}
          </div>
        </div>
        <span class="${claseEstadoPresupuesto(p.estado_efectivo)}">${
          ETIQUETA_ESTADO_PRESUPUESTO[p.estado_efectivo]
        }</span>
      </div>
      <div class="mt-2.5 flex items-center justify-between gap-2">
        <span class="text-lg font-semibold">${formatearMonto(p.total, p.moneda)}</span>
        <span class="text-xs text-apagado">${p.items} ${p.items === 1 ? "renglón" : "renglones"}</span>
      </div>
    </a>`;
}

export async function renderPresupuestos(contenedor: HTMLElement): Promise<void> {
  async function pintar(): Promise<void> {
    const [presupuestos, clientes, proyectos] = await Promise.all([
      api.get<Presupuesto[]>("/api/presupuestos"),
      api.get<Cliente[]>("/api/clientes"),
      api.get<ProyectoListado[]>("/api/proyectos"),
    ]);

    contenedor.innerHTML = `
      <h1 class="titulo-pagina">Presupuestos</h1>
      <p class="subtitulo">${presupuestos.length} en total</p>
      ${conmutadorPrecios("presupuestos")}

      <div class="mt-4 space-y-2">
        ${
          presupuestos.length
            ? presupuestos.map(fila).join("")
            : `<div class="tarjeta text-center text-apagado">Todavía no hay presupuestos.</div>`
        }
      </div>

      ${
        clientes.length
          ? `<details class="tarjeta mt-4">
        <summary class="flex items-center gap-2 font-medium text-acento">${iconoMas} Presupuesto nuevo</summary>
        <form id="fNuevo" class="mt-4 space-y-3">
          <label><span class="etiqueta">Cliente</span>
            <select id="cliente" class="campo" required>
              ${clientes.map((c) => `<option value="${c.id}">${esc(c.nombre)}</option>`).join("")}
            </select></label>
          <label><span class="etiqueta">Proyecto (opcional)</span>
            <select id="proyecto" class="campo">
              <option value="">Ninguno todavía</option>
              ${proyectos.map((p) => `<option value="${p.id}">${esc(p.nombre)}</option>`).join("")}
            </select></label>
          <div class="grid grid-cols-2 gap-3">
            <label><span class="etiqueta">Moneda</span>
              <select id="moneda" class="campo">
                ${MONEDAS.map((m) => `<option value="${m}">${m}</option>`).join("")}
              </select></label>
            <label><span class="etiqueta">Validez (días)</span>
              <input id="validez" type="number" class="campo" value="15" min="1" max="365" /></label>
          </div>
          <p id="errNuevo" class="text-sm text-alerta"></p>
          <button class="btn-primario w-full">Crear borrador</button>
        </form>
      </details>`
          : `<div class="tarjeta mt-4 text-sm text-apagado">
               Para armar un presupuesto primero necesitás un cliente.
               <a href="#/clientes" class="text-acento hover:underline">Cargá uno acá</a>.
             </div>`
      }`;

    const form = contenedor.querySelector("#fNuevo") as HTMLFormElement | null;
    form?.addEventListener("submit", async (ev) => {
      ev.preventDefault();
      const err = contenedor.querySelector("#errNuevo") as HTMLElement;
      err.textContent = "";
      const proyecto = (contenedor.querySelector("#proyecto") as HTMLSelectElement).value;
      try {
        const creado = await api.post<Presupuesto>("/api/presupuestos", {
          cliente_id: Number((contenedor.querySelector("#cliente") as HTMLSelectElement).value),
          proyecto_id: proyecto ? Number(proyecto) : null,
          moneda: (contenedor.querySelector("#moneda") as HTMLSelectElement).value,
          validez_dias: Number((contenedor.querySelector("#validez") as HTMLInputElement).value),
        });
        location.hash = `#/presupuesto/${creado.id}`;
      } catch (e) {
        err.textContent = e instanceof Error ? e.message : "No se pudo crear";
      }
    });
  }

  try {
    await pintar();
  } catch (e) {
    contenedor.innerHTML = `<div class="tarjeta text-alerta">No se pudieron cargar los presupuestos: ${esc(
      e instanceof Error ? e.message : "error desconocido",
    )}</div>`;
  }
}
